import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel,
  IconButton, Tooltip, LinearProgress, CircularProgress, Avatar, Autocomplete, Chip, Paper
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import GroupsIcon from '@mui/icons-material/Groups';
import FilterListIcon from '@mui/icons-material/FilterList';
import AssignmentIcon from '@mui/icons-material/Assignment';
import API_ENDPOINTS from '../config/apiConfig';
import { useTranslation } from '../i18n/i18n';
import { useBranch } from '../context/BranchContext';
import { DAY_KEYS, getMonday, addDays, getTodayISO, formatDateISO } from '../utils/dateUtils';

const TIMELINE_START = 8;
const TIMELINE_END = 20;
const TOTAL_HOURS = TIMELINE_END - TIMELINE_START;

const POSITION_COLORS = [
  '#1B4965', '#62B6CB', '#FF715B', '#4CAF50', '#FF9800',
  '#9C27B0', '#00BCD4', '#E91E63', '#8BC34A', '#FF5722'
];

const HOURS = Array.from({ length: TOTAL_HOURS + 1 }, (_, i) => TIMELINE_START + i);

function getDayKeyFromDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const jsDay = d.getDay();
  return DAY_KEYS[(jsDay + 6) % 7];
}

function formatHourLabel(hour) {
  if (hour === 0 || hour === 12) return hour === 0 ? '12 AM' : '12 PM';
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
}

function formatTime12h(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const ampm = h < 12 ? 'AM' : 'PM';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${hour12}:00 ${ampm}` : `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function parseTimeToHours(time24) {
  if (!time24) return 0;
  const [h, m] = time24.split(':').map(Number);
  return h + m / 60;
}

function computeStaffingDensity(filteredEmployees) {
  const density = new Array(TOTAL_HOURS).fill(0);
  filteredEmployees.forEach(({ dayData }) => {
    if (!dayData || dayData.active === false) return;
    const startH = parseTimeToHours(dayData.start);
    const endH = parseTimeToHours(dayData.end);
    for (let h = TIMELINE_START; h < TIMELINE_END; h++) {
      if (startH <= h && endH > h) {
        density[h - TIMELINE_START]++;
      }
    }
  });
  return density;
}

export default function BranchStaffing({ employees }) {
  const { t, lang } = useTranslation();
  const { branches: allBranches } = useBranch();
  const [selectedDate, setSelectedDate] = useState(getTodayISO());
  const [selectedBranch, setSelectedBranch] = useState('');
  const [positionFilter, setPositionFilter] = useState('all');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [schedules, setSchedules] = useState(new Map());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (allBranches && allBranches.length > 0 && !selectedBranch) {
      setSelectedBranch(allBranches[0].branch_id || allBranches[0].code || allBranches[0]._id || '');
    }
  }, [allBranches, selectedBranch]);

  const weekStart = useMemo(() => getMonday(selectedDate), [selectedDate]);
  const dayKey = useMemo(() => getDayKeyFromDate(selectedDate), [selectedDate]);

  useEffect(() => {
    if (!employees || employees.length === 0) return;
    let cancelled = false;
    setLoading(true);

    Promise.all(
      employees.map(emp =>
        fetch(`${API_ENDPOINTS.SCHEDULE_EFFECTIVE(emp.email)}?week_start=${weekStart}`, { credentials: 'include' })
          .then(r => (r.ok ? r.json() : null))
          .then(data => ({ email: emp.email, ...data }))
          .catch(() => ({ email: emp.email, schedule: null }))
      )
    ).then(results => {
      if (cancelled) return;
      const map = new Map();
      results.forEach(r => map.set(r.email, r));
      setSchedules(map);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [employees, weekStart]);

  const getBranchInfo = useCallback((branchId) => {
    if (!allBranches) return null;
    return allBranches.find(b => (b.branch_id || b.code || b._id) === branchId) || null;
  }, [allBranches]);

  const employeeMap = useMemo(() => {
    const map = new Map();
    (employees || []).forEach(e => map.set(e.email, e));
    return map;
  }, [employees]);

  const allPositions = useMemo(() => {
    const set = new Set();
    (employees || []).forEach(e => { if (e.position) set.add(e.position); });
    return [...set].sort();
  }, [employees]);

  const getPositionColor = useCallback((position) => {
    const idx = allPositions.indexOf(position);
    return POSITION_COLORS[idx >= 0 ? idx % POSITION_COLORS.length : 0];
  }, [allPositions]);

  const filteredEmployees = useMemo(() => {
    const result = [];
    (employees || []).forEach(emp => {
      const scheduleData = schedules.get(emp.email);
      const schedule = scheduleData?.schedule;
      if (!schedule?.days) return;
      const dayData = schedule.days[dayKey];
      if (!dayData || dayData.active === false) return;
      if (dayData.branch !== selectedBranch) return;
      if (positionFilter !== 'all' && emp.position !== positionFilter) return;
      if (employeeFilter && emp.email !== employeeFilter) return;
      result.push({ emp, dayData });
    });
    result.sort((a, b) => {
      const posCmp = (a.emp.position || '').localeCompare(b.emp.position || '');
      if (posCmp !== 0) return posCmp;
      return parseTimeToHours(a.dayData.start) - parseTimeToHours(b.dayData.start);
    });
    return result;
  }, [employees, schedules, dayKey, selectedBranch, positionFilter, employeeFilter]);

  const groupedByPosition = useMemo(() => {
    const groups = new Map();
    filteredEmployees.forEach(item => {
      const pos = item.emp.position || 'Unassigned';
      if (!groups.has(pos)) groups.set(pos, []);
      groups.get(pos).push(item);
    });
    return groups;
  }, [filteredEmployees]);

  const density = useMemo(() => computeStaffingDensity(filteredEmployees), [filteredEmployees]);
  const peakCount = useMemo(() => Math.max(...density, 0), [density]);
  const peakHourIndex = useMemo(() => density.indexOf(peakCount), [density, peakCount]);

  const branchName = useMemo(() => {
    const info = getBranchInfo(selectedBranch);
    return info ? info.name : selectedBranch;
  }, [selectedBranch, getBranchInfo]);

  const locale = lang === 'spanish' ? 'es-MX' : 'en-US';
  const formattedDate = useMemo(() => {
    const d = new Date(selectedDate + 'T00:00:00');
    return d.toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }, [selectedDate, locale]);

  const navigateDay = useCallback((offset) => {
    setSelectedDate(prev => addDays(prev, offset));
  }, []);

  const goToToday = useCallback(() => {
    setSelectedDate(getTodayISO());
  }, []);

  const employeeOptions = useMemo(() =>
    (employees || []).map(e => ({ label: e.fullname || e.email, email: e.email })),
    [employees]
  );

  if (!allBranches || allBranches.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', color: 'white' }}>
            <AssignmentIcon sx={{ fontSize: 22 }} />
          </Avatar>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {t('branch_staffing_title') || 'Branch Staffing'}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 640, ml: 7 }}>
          {t('branch_staffing_subtitle') || 'View daily staffing for a specific branch. This is a read-only visualization.'}
        </Typography>
      </Box>

      {/* Controls Row 1: Branch + Date */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 }, maxWidth: 280, flex: 1 }}>
            <InputLabel>{t('branch_staffing_branch_label') || 'Branch'}</InputLabel>
            <Select
              value={selectedBranch}
              label={t('branch_staffing_branch_label') || 'Branch'}
              onChange={(e) => setSelectedBranch(e.target.value)}
            >
              {allBranches.map((b) => (
                <MenuItem key={b.branch_id || b.code || b._id} value={b.branch_id || b.code || b._id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton onClick={() => navigateDay(-1)} size="small">
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="body2" sx={{ minWidth: { xs: 0, sm: 200 }, textAlign: 'center', fontWeight: 500 }}>
              {formattedDate}
            </Typography>
            <IconButton onClick={() => navigateDay(1)} size="small">
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <TextField
            type="date"
            size="small"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />

          <IconButton onClick={goToToday} size="small" title={t('branch_staffing_today') || 'Today'}>
            <TodayIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Controls Row 2: Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 }, maxWidth: 250 }}>
            <InputLabel>{t('branch_staffing_position_filter') || 'Position'}</InputLabel>
            <Select
              value={positionFilter}
              label={t('branch_staffing_position_filter') || 'Position'}
              onChange={(e) => setPositionFilter(e.target.value)}
            >
              <MenuItem value="all">{t('branch_staffing_all_positions') || 'All Positions'}</MenuItem>
              {allPositions.map(p => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            size="small"
            options={employeeOptions}
            getOptionLabel={(opt) => opt.label}
            value={employeeOptions.find(o => o.email === employeeFilter) || null}
            onChange={(_, val) => setEmployeeFilter(val ? val.email : '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('branch_staffing_employee_filter') || 'Employee'}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <FilterListIcon fontSize="small" sx={{ color: '#62B6CB', mr: 0.5 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ minWidth: 220, maxWidth: 320 }}
          />

          {filteredEmployees.length > 0 && (
            <Chip
              label={`${filteredEmployees.length} employee${filteredEmployees.length !== 1 ? 's' : ''}`}
              size="small"
              sx={{ height: 24, fontWeight: 500 }}
            />
          )}
        </Box>
      </Paper>

      {/* Loading indicator */}
      {loading && <LinearProgress sx={{ height: 3, borderRadius: 1, mb: 2 }} />}

      {/* Summary */}
      {!loading && (
        <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
            {filteredEmployees.length} Employee{filteredEmployees.length !== 1 ? 's' : ''} Scheduled
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {groupedByPosition.size} Position{groupedByPosition.size !== 1 ? 's' : ''}
          </Typography>
          {peakCount > 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Peak: {peakCount} employee{peakCount !== 1 ? 's' : ''} ({formatHourLabel(peakHourIndex + TIMELINE_START)})
            </Typography>
          )}
        </Box>
      )}

      {/* Gantt Chart */}
      {!loading && filteredEmployees.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
          <Avatar sx={{ width: 64, height: 64, mb: 2, bgcolor: 'grey.100', color: 'grey.400', mx: 'auto' }}>
            <GroupsIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {(positionFilter !== 'all' || employeeFilter)
              ? (t('branch_staffing_no_match') || 'No employees match the selected filters.')
              : (t('branch_staffing_no_employees') || 'No employees are scheduled at this branch for this date.')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {(positionFilter !== 'all' || employeeFilter)
              ? 'Try adjusting your filter criteria.'
              : 'Try selecting a different date or branch.'}
          </Typography>
        </Box>
      ) : !loading ? (
        <Paper elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
          {/* Time axis header */}
          <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ width: 140, minWidth: 140, p: 1.5, bgcolor: 'grey.50', borderRight: 1, borderColor: 'divider' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                {t('branch_staffing_employee_filter') || 'Employee'}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, position: 'relative', height: 40, bgcolor: 'grey.50' }}>
              {HOURS.map((hour) => {
                const leftPct = ((hour - TIMELINE_START) / TOTAL_HOURS) * 100;
                return (
                  <Typography
                    key={hour}
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      left: `${leftPct}%`,
                      transform: 'translateX(-50%)',
                      fontWeight: 500,
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      top: '50%',
                      mt: '-8px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {formatHourLabel(hour)}
                  </Typography>
                );
              })}
            </Box>
          </Box>

          {/* Position groups */}
          {[...groupedByPosition.entries()].map(([position, items]) => (
            <Box key={position}>
              {/* Position header */}
              <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ width: 140, minWidth: 140, px: 1.5, py: 0.75, bgcolor: getPositionColor(position) + '0D', borderRight: 1, borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: getPositionColor(position), textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                    {position}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, px: 1.5, py: 0.75, bgcolor: getPositionColor(position) + '0D' }}>
                  <Typography variant="caption" sx={{ fontWeight: 500, color: getPositionColor(position) }}>
                    {items.length} employee{items.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </Box>

              {/* Employee rows */}
              {items.map(({ emp, dayData }) => (
                <EmployeeRow
                  key={emp.email}
                  emp={emp}
                  dayData={dayData}
                  color={getPositionColor(position)}
                  branchName={branchName}
                  locale={locale}
                  getBranchInfo={getBranchInfo}
                />
              ))}
            </Box>
          ))}

          {/* Staffing Density */}
          <Box sx={{ display: 'flex', borderTop: 2, borderColor: 'divider' }}>
            <Box sx={{ width: 140, minWidth: 140, p: 1.5, bgcolor: 'grey.50', borderRight: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                Density
              </Typography>
            </Box>
            <Box sx={{ flex: 1, position: 'relative', height: 36, bgcolor: 'grey.50' }}>
              {density.map((count, i) => {
                const leftPct = (i / TOTAL_HOURS) * 100;
                const widthPct = (1 / TOTAL_HOURS) * 100;
                return (
                  <Box
                    key={i}
                    sx={{
                      position: 'absolute',
                      left: `${leftPct}%`,
                      width: `${widthPct}%`,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRight: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: count === peakCount && count > 0 ? 700 : 400, color: count === peakCount && count > 0 ? 'primary.main' : 'text.secondary', fontSize: '0.65rem' }}>
                      {count}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Paper>
      ) : null}
    </Box>
  );
}

function EmployeeRow({ emp, dayData, color, branchName, locale, getBranchInfo }) {
  const startH = parseTimeToHours(dayData.start);
  const endH = parseTimeToHours(dayData.end);
  const clampedStart = Math.max(startH, TIMELINE_START);
  const clampedEnd = Math.min(endH, TIMELINE_END);
  const leftPct = ((clampedStart - TIMELINE_START) / TOTAL_HOURS) * 100;
  const widthPct = ((clampedEnd - clampedStart) / TOTAL_HOURS) * 100;
  const showName = widthPct > 8;

  const empBranchInfo = getBranchInfo(dayData.branch);
  const empBranchName = empBranchInfo ? empBranchInfo.name : (dayData.branch || branchName);

  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{emp.fullname || emp.email}</Typography>
      <Typography variant="caption" display="block">{emp.position}</Typography>
      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
        {formatTime12h(dayData.start)} – {formatTime12h(dayData.end)}
      </Typography>
      {empBranchName && (
        <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.8)' }}>{empBranchName}</Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
      <Box sx={{ width: 140, minWidth: 140, px: 1.5, py: 1, borderRight: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {emp.fullname || emp.email}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, position: 'relative', height: 44, '&:hover': { bgcolor: 'grey.50' } }}>
        {/* Hour grid lines */}
        {HOURS.slice(0, -1).map((hour) => {
          const leftPct = ((hour - TIMELINE_START) / TOTAL_HOURS) * 100;
          return (
            <Box
              key={hour}
              sx={{
                position: 'absolute',
                left: `${leftPct}%`,
                width: '1px',
                height: '100%',
                bgcolor: 'divider'
              }}
            />
          );
        })}

        {/* Shift block */}
        <Tooltip title={tooltipContent} arrow placement="top">
          <Box
            sx={{
              position: 'absolute',
              left: `${leftPct}%`,
              width: `${widthPct}%`,
              boxSizing: 'border-box',
              top: 6,
              bottom: 6,
              bgcolor: color,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              px: showName ? 1 : 0.5,
              cursor: 'default',
              boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
              '&:hover': { boxShadow: '0 2px 6px rgba(0,0,0,0.25)', filter: 'brightness(1.1)' },
              transition: 'box-shadow 0.15s, filter 0.15s'
            }}
          >
            {showName && (
              <Typography
                variant="caption"
                sx={{
                  color: 'white',
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2
                }}
              >
                {emp.fullname || emp.email}
              </Typography>
            )}
          </Box>
        </Tooltip>

        {/* Time labels below block */}
        <Box sx={{ position: 'absolute', bottom: 2, left: `${leftPct}%`, width: `${widthPct}%`, display: 'flex', justifyContent: 'space-between', px: 0.5 }}>
          <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', lineHeight: 1 }}>
            {formatTime12h(dayData.start)}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', lineHeight: 1 }}>
            {formatTime12h(dayData.end)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
